import Link from 'next/link';

import styles from '@/components/agents/agents.module.css';

import clsxm from '@/lib/clsxm';

import {
  ArrowGlyph,
  buttonSecondary,
  delay,
  SectionHeading,
  tone,
} from '@/components/agents/primitives';
import { useReveal } from '@/components/agents/useReveal';

import { AUDIOS_IN_PLAN } from '@/constants/common';

import { useLandingT } from './kit';

const formatCount = (value: number, lang: string) =>
  new Intl.NumberFormat(lang === 'ar' ? 'ar-u-nu-latn' : lang).format(value);

/**
 * How transcriptions are counted: per file, whatever its length. Numbers come
 * from the plan limits, so they follow any change there.
 */
export default function Plans() {
  const { t, lang } = useLandingT();
  const reveal = useReveal<HTMLDivElement>(0.2);

  const stops = [
    {
      plan: 'Free',
      count: AUDIOS_IN_PLAN.Free,
      note: t('transcriptions every month, free'),
    },
    {
      plan: 'Starter',
      count: AUDIOS_IN_PLAN.Starter,
      note: t('transcriptions a month'),
    },
    {
      plan: 'Unlimited',
      count: AUDIOS_IN_PLAN.Unlimited,
      note: t('transcriptions a month'),
    },
    {
      plan: 'Unlimited 5000',
      count: AUDIOS_IN_PLAN['Unlimited 5000'],
      note: t('transcriptions a month on our largest plan'),
    },
  ];

  return (
    <section className='py-20 sm:py-24'>
      <div className='mx-auto max-w-7xl px-6 lg:px-8'>
        <div className='grid gap-12 lg:grid-cols-12 lg:items-end'>
          <div className='lg:col-span-7'>
            <SectionHeading
              size='md'
              title={t('Priced per file.')}
              tail={t('Not per minute.')}
              lead={t(
                'A two-minute voice note and an hour-long interview each count as one transcription, so long recordings never cost more.',
              )}
            />
          </div>
          <div className='lg:col-span-5 lg:text-end'>
            <Link href='/pricing' className={buttonSecondary}>
              {t('Compare plans')}
              <ArrowGlyph />
            </Link>
          </div>
        </div>

        <div
          ref={reveal.ref}
          data-reveal={reveal.state}
          className={clsxm(
            styles.reveal,
            'mt-14 grid grid-cols-2 gap-x-6 gap-y-10 border-t border-[#0f0f28]/[0.1] pt-10 lg:grid-cols-4 dark:border-white/[0.1]',
          )}
        >
          {stops.map((stop, index) => (
            <div
              key={stop.plan}
              className={styles.rv}
              style={delay(index * 90)}
            >
              <p className={clsxm('text-sm font-semibold', tone.quiet)}>
                {t(stop.plan)}
              </p>
              <p
                className={clsxm(
                  styles.display,
                  'mt-3 text-[2.75rem] font-semibold leading-none tabular-nums sm:text-[3.25rem]',
                  index === 0
                    ? 'text-primary-600 dark:text-primary-400'
                    : tone.ink,
                )}
              >
                {formatCount(stop.count, lang)}
              </p>
              <p className={clsxm('mt-3 text-[15px]', tone.body)}>
                {stop.note}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
